import { Component, ErrorInfo, ReactNode } from 'react';
import ErrorFallback from './ErrorFallback';
import { isDynamicImportError, recoverFromStaleChunk } from '../utils/chunkReload';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  /** Stale code-split chunk after a deploy — self-heal via reload, no error UI. */
  staleChunk: boolean;
}

/**
 * Error Boundary Component
 * Catches JavaScript errors in child component tree and displays a fallback UI.
 * Stale-deploy chunk failures are self-healed with an "Updating…" overlay +
 * reload instead of the error fallback (see utils/chunkReload.ts).
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, staleChunk: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, staleChunk: isDynamicImportError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (isDynamicImportError(error)) {
      // Not a real crash — a new version was deployed while this tab was open.
      console.info('Stale chunk after deploy — refreshing to latest version:', error.message);
      const reloading = recoverFromStaleChunk();
      if (!reloading) {
        // Loop guard suppressed the reload — fall back to the normal error UI.
        this.setState({ staleChunk: false });
      }
      return;
    }
    // Log error to console (could also send to error tracking service)
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, staleChunk: false });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Stale deploy — the overlay + reload are already underway; render a
      // matching quiet state instead of the error page.
      if (this.state.staleChunk) {
        return (
          <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 p-8 text-center">
            <div
              className="w-10 h-10 rounded-full border-4 border-gray-200 animate-spin"
              style={{ borderTopColor: '#6eb041' }}
            />
            <p className="text-gray-700 font-semibold">Updating to the latest version</p>
            <p className="text-gray-500 text-sm">One moment — refreshing the page…</p>
          </div>
        );
      }

      return (
        <ErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
